import { forwardRef, useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';

import { Input, type InputProps } from './Input';
import styles from './PasswordInput.module.css';

export interface PasswordInputProps extends Omit<InputProps, 'type'> {
  /** Warns while Caps Lock is on. Off for a confirmation field, where the first one already says it. */
  capsLockHint?: boolean | undefined;
}

/**
 * A password field that can be read back: "Mostrar" turns the dots into text and "Ocultar" turns
 * them back. Said in words rather than with an eye icon, which not everyone reads as "show".
 *
 * The toggle keeps the caret in the field — pressing it must not cost the user their place — and
 * the field goes back to dots on its own when the form is submitted, so a password never stays on
 * screen for the next person at a shared counter.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ capsLockHint = true, onKeyDown, onKeyUp, onBlur, ...rest }, ref) {
    const [visible, setVisible] = useState(false);
    const [capsLock, setCapsLock] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const setRefs = useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref !== null) {
          ref.current = node;
        }
      },
      [ref],
    );

    useEffect(() => {
      const form = inputRef.current?.form;
      if (!form) {
        return;
      }
      const hide = () => {
        setVisible(false);
      };
      form.addEventListener('submit', hide);
      return () => {
        form.removeEventListener('submit', hide);
      };
    }, []);

    const readCapsLock = (event: KeyboardEvent<HTMLInputElement>) => {
      if (capsLockHint) {
        setCapsLock(event.getModifierState('CapsLock'));
      }
    };

    return (
      <>
        <div className={styles.wrapper}>
          <Input
            {...rest}
            ref={setRefs}
            type={visible ? 'text' : 'password'}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className={styles.input}
            onKeyDown={(event) => {
              readCapsLock(event);
              onKeyDown?.(event);
            }}
            onKeyUp={(event) => {
              readCapsLock(event);
              onKeyUp?.(event);
            }}
            onBlur={(event) => {
              setCapsLock(false);
              onBlur?.(event);
            }}
          />
          <button
            type="button"
            className={styles.toggle}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={() => {
              setVisible((current) => !current);
            }}
            disabled={rest.disabled}
          >
            {visible ? 'Ocultar' : 'Mostrar'}
            <span className="visually-hidden"> senha</span>
          </button>
        </div>
        {capsLock ? (
          <p className={styles.capsLock} role="status">
            <span aria-hidden="true">⇪</span> Caps Lock está ativado
          </p>
        ) : null}
      </>
    );
  },
);
